import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * Fetch a header section once and share the response between the mobile menu
 * and the desktop localization selector.
 * @param {string} sectionId
 * @returns {Promise<string>}
 */
function requestHeaderSection(sectionId) {
  const url = new URL(window.location.href);
  url.searchParams.set('section_id', sectionId);

  const globalWindow =
    /** @type {Window & { __cbnHeaderSectionRequests?: Map<string, Promise<string>> }} */ (window);
  const cache = (globalWindow.__cbnHeaderSectionRequests ||= new Map());
  const cacheKey = url.toString();
  const cached = cache.get(cacheKey);

  if (cached) return cached;

  const request = fetch(cacheKey, {
    credentials: 'same-origin',
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Header section request failed with status ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      cache.delete(cacheKey);
      throw error;
    });

  cache.set(cacheKey, request);
  return request;
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.refs.details.addEventListener('keydown', this.#onSummaryKeyDown);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.refs.details.removeEventListener('keydown', this.#onSummaryKeyDown);
  }

  /**
   * Keep the main drawer operable with Enter and Space.
   * The theme click action and the native details toggle can otherwise cancel
   * one another when the summary is activated from the keyboard.
   * @param {KeyboardEvent} event
   */
  #onSummaryKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const summary = this.refs.details.querySelector(':scope > summary');

    if (event.target !== summary) return;

    event.preventDefault();

    if (this.isOpen) {
      this.close();
      return;
    }

    this.refs.details.setAttribute('open', '');
    this.open(undefined, event);
  };

  /** @type {Promise<void> | undefined} */
  #hydrationPromise;

  /**
   * Replace the lightweight fallback with the complete drawer returned by
   * Shopify's Section Rendering API.
   * @returns {Promise<void>}
   */
  #hydrateDrawer() {
    if (!this.hasAttribute('data-lazy-content')) return Promise.resolve();
    if (this.#hydrationPromise) return this.#hydrationPromise;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return Promise.resolve();

    this.#hydrationPromise = requestHeaderSection(sectionId)
      .then((html) => {
        if (!this.isConnected || !this.hasAttribute('data-lazy-content')) return;

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const sourceComponent = parsed.querySelector(
          `header-drawer[data-section-id="${CSS.escape(sectionId)}"]:not([data-lazy-content])`
        );
        const sourceDrawer = sourceComponent?.querySelector('[ref="menuDrawer"]');

        if (!(sourceDrawer instanceof HTMLElement)) {
          throw new Error('Deferred header drawer content was not found');
        }

        const fragment = document.createDocumentFragment();
        for (const child of sourceDrawer.children) {
          if (child.tagName === 'SCRIPT') continue;
          fragment.append(document.importNode(child, true));
        }

        this.refs.menuDrawer.replaceChildren(fragment);
        this.removeAttribute('data-lazy-content');
        this.updatedCallback();
        this.#setupAnimatedElementListeners();
      })
      .catch((error) => {
        this.#hydrationPromise = undefined;
        console.warn('Crafty deferred mobile menu could not be loaded.', error);
      });

    return this.#hydrationPromise;
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);

    if (this.hasAttribute('data-lazy-content')) {
      this.#hydrateDrawer().then(() => {
        if (!details.hasAttribute('open')) return;
        this.#openDrawer(details, target);
      });
      return;
    }

    this.#openDrawer(details, target);
  }

  /**
   * Apply the existing drawer behavior after deferred content is ready. The
   * lightweight fallback still opens if Shopify's deferred request ever fails.
   * @param {HTMLDetailsElement} details
   * @param {string} [target]
   */
  #openDrawer(details, target) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    this.#loadImages(details);
    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Start loading drawer imagery only when the customer opens the drawer.
   * @param {HTMLDetailsElement} details
   */
  #loadImages(details) {
    const images = details.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
